import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { RefreshCw, Download, Upload, ArrowLeftRight } from "lucide-react";

export default function HROneSync() {
  const [pending, setPending] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [reqs, setReqs] = useState([]);
  const [pushReq, setPushReq] = useState("");
  const [pushStatus, setPushStatus] = useState("Sourcing");

  const load = () => {
    api.get("/hrone/pending-pull").then((r) => setPending(r.data)).catch(() => {});
    api.get("/hrone/statuses").then((r) => setStatuses(r.data));
    api.get("/hrone/logs").then((r) => setLogs(r.data)).catch(() => {});
    api.get("/requisitions").then((r) => {
      setReqs(r.data);
      if (r.data[0]) setPushReq(r.data[0].id);
    });
  };
  useEffect(() => { load(); }, []);

  const pull = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/hrone/pull", { limit: 3 });
      toast.success(`${data.pulled} requisitions pulled from HROne`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const push = async () => {
    try {
      await api.post("/hrone/push-status", { requisition_id: pushReq, hrone_status: pushStatus });
      toast.success(`Pushed '${pushStatus}' to HROne`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="hrone-sync-page">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[#E05A47] font-bold">System of Record</div>
      <h1 className="font-heading font-black text-3xl tracking-tight mt-1 flex items-center gap-3">
        <ArrowLeftRight size={26} className="text-[#0A251D]" /> HROne Sync
      </h1>
      <p className="text-sm text-muted-foreground mt-1">HROne remains the source of truth for requisitions. TalentOS AI pulls open reqs, runs the recruitment automation, and pushes status changes back — all logged and auditable.</p>

      <div className="grid lg:grid-cols-2 gap-4 mt-6">
        <div className="border bg-white rounded-xl p-6" data-testid="hrone-pull-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold"><Download size={14} /> Pull open requisitions</div>
            <button data-testid="hrone-pull-button" onClick={pull} disabled={busy || pending.length === 0}
              className="bg-[#0A251D] text-white px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-[#133E31] transition-colors duration-150 disabled:opacity-40 flex items-center gap-1.5">
              <RefreshCw size={12} /> {busy ? "Pulling…" : `Pull ${pending.length}`}
            </button>
          </div>
          {pending.length === 0 && <div className="text-sm text-muted-foreground">All HROne requisitions have been imported. Nothing pending.</div>}
          {pending.map((p) => (
            <div key={p.hrone_id} className="border-b last:border-0 py-2.5" data-testid={`pending-${p.hrone_id}`}>
              <div className="flex items-center justify-between">
                <div className="font-semibold text-sm">{p.title}</div>
                <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 px-2 py-0.5 rounded font-bold">{p.hrone_id}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">{p.department} · {p.location} · {p.experience_min}–{p.experience_max} yrs · ₹{p.salary_min}–{p.salary_max} LPA · HM: {p.hiring_manager}</div>
            </div>
          ))}
        </div>

        <div className="border bg-white rounded-xl p-6" data-testid="hrone-push-panel">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-3"><Upload size={14} /> Push status back to HROne</div>
          <select data-testid="push-req-select" value={pushReq} onChange={(e) => setPushReq(e.target.value)} className="w-full border px-3 py-2 text-sm bg-white mb-2">
            {reqs.map((r) => <option key={r.id} value={r.id}>{r.title} {r.hrone_requisition_id ? `(${r.hrone_requisition_id})` : ""} — {r.status}</option>)}
          </select>
          <select data-testid="push-status-select" value={pushStatus} onChange={(e) => setPushStatus(e.target.value)} className="w-full border px-3 py-2 text-sm bg-white mb-3">
            {statuses.map((s) => <option key={s}>{s}</option>)}
          </select>
          <button data-testid="push-status-button" onClick={push} className="w-full bg-[#E05A47] text-white py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-[#C74936] transition-colors duration-150">
            Push to HROne
          </button>
        </div>
      </div>

      <div className="border bg-white rounded-xl p-6 mt-4" data-testid="hrone-logs">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-3">Sync History</div>
        {logs.length === 0 && <div className="text-sm text-muted-foreground">No syncs yet.</div>}
        {logs.map((l) => (
          <div key={l.id} className="flex items-center justify-between border-b last:border-0 py-2 text-sm">
            <div className="flex flex-col">
              <span className="font-semibold capitalize">{l.type?.replace(/_/g, " ")}</span>
              <span className="text-xs text-muted-foreground">{new Date(l.timestamp).toLocaleString()} by {l.triggered_by || "—"} · {l.detail || `${l.records} record(s)`}</span>
            </div>
            <span className="text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded font-bold">{l.status}</span>
          </div>
        ))}
      </div>

      <Link to="/integrations" className="inline-block mt-4 text-xs font-bold text-[#E05A47] hover:underline">← Back to all integrations</Link>
    </div>
  );
}
